<template>
  <div class="viewer" @click.self="toCloseSlider">
    <ButtonClose class="viewer__close" @click="toCloseSlider"/>
    <ButtonSliderNavigation class="viewer__btn viewer__btn--left" :type="'left'" @click="prevSlide()" v-if="rawSlides.length > 1"/>
    <ul class="viewer__list" ref="list" @click.self="toCloseSlider">
      <template v-for="(item, index) in DATA">
        <li class="viewer__item" v-if="item.type === 'video'" @click.self="toCloseSlider">
          <VideoSlide :ref="el => setVideoRef(el, index)" :data="item" />
        </li>
        <li class="viewer__item" v-else @click="toCloseSlider">
          <ImageSlide :image="item" />
        </li>
      </template>
    </ul>
    <ButtonSliderNavigation class="viewer__btn viewer__btn--right" :type="'right'" @click="nextSlide()" v-if="rawSlides.length > 1"/>
  </div>
</template>

<script setup>
  import { onMounted, ref, nextTick, onBeforeUnmount } from 'vue';
  import { useStore } from 'vuex';
  //-----------------------------------
  import ImageSlide from '../../ui/ImageSlide/ImageSlide.vue';
  import VideoSlide from '../../ui/VideoSlide/VideoSlide.vue';
  import ButtonSliderNavigation from '../../ui/ButtonSliderNavigation/ButtonSliderNavigation.vue';
  import DROPDOWN_NAMES from '@shared/constants/dropdowns/dropdownsName';
  import ButtonClose from '../../ui/ButtonClose/ButtonClose.vue';
  //-----------------------------------
  const store = useStore();
  //-----------------------------------
  const rawSlides = [...store.state.Slider.slides];
  const DATA = [rawSlides[rawSlides.length - 1], ...rawSlides, rawSlides[0]];
  //-----------------------------------
  const list = ref(null);
  const currentIndex = ref(store.state.Slider.startIndex + 1);
  const videoRefs = ref([]);
  //-----------------------------------
  let currentOffsetSize = 0;
  let transitioning = false;
  //-----------------------------------
  onMounted(async () => {
    await nextTick();
    defineOffsetSize();
    moveSlider(false);
    await new Promise(resolve => setTimeout(resolve, 200));
    addTransitionForSlider();
    addEventListeners();
  });
  onBeforeUnmount(() => {
    window.removeEventListener('resize', handleResize);
    window.removeEventListener('keydown', onKeyDown);
    window.removeEventListener('wheel', onWheel, { passive: true });
  })
  //-----------------------------------
  //Добавить слушатели событий
  const addEventListeners = () => {
    window.addEventListener('resize', handleResize);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('wheel', onWheel, { passive: true });
  }
  //Передвинуть карусель
  const moveSlider = async (withTransition = true) => {
    const offset = currentOffsetSize * currentIndex.value;
    list.value.querySelectorAll('.viewer__item').forEach(item => {
      item.style.transition = withTransition ? `transform .3s ease` : 'none';
      item.style.transform = `translateX(-${offset}px)`;
    });
    await nextTick();
    updateVideoPlayback();
  };
  //Определение размера смещения слайдов при навигации
  const defineOffsetSize = () => {
    currentOffsetSize = list.value.querySelector('.viewer__item').scrollWidth;
  };
  //Следующая страница
  const nextSlide = () => {
    if (transitioning) return;
    transitioning = true;
    currentIndex.value++;
    moveSlider(true);
    setTimeout(() => {
      if (currentIndex.value === DATA.length - 1) {
        currentIndex.value = 1;
        moveSlider(false);
      }
      transitioning = false;
    }, 350);
  };
  //Предыдущая страница
  const prevSlide = () => {
    if (transitioning) return;
    transitioning = true;
    currentIndex.value--;
    moveSlider(true);
    setTimeout(() => {
      if (currentIndex.value === 0) {
        currentIndex.value = DATA.length - 2;
        moveSlider(false);
      }
      transitioning = false;
    }, 350);
  };
  //Добавить плавный переход для слайдера
  const addTransitionForSlider = () => {
    list.value.querySelectorAll('.viewer__item').forEach(item => {
      item.style.transition = `transform .3s ease`;
    });
  };
  //Воспроизводим / останавливаем видео
  const updateVideoPlayback = () => {
    const currentSlide = DATA[currentIndex.value];
    videoRefs.value.forEach((videoRef, idx) => {
      if (!videoRef || typeof videoRef.play !== 'function' || typeof videoRef.pause !== 'function') return;

      if (idx === currentIndex.value && currentSlide.type === 'video') {
        videoRef.play();
      } else {
        videoRef.pause();
      }
    });
  };
  //Обновляет реактивный массив videoRefs, сохраняя ссылку на компонент VideoSlide по индексу слайда
  const setVideoRef = (el, index) => {
    videoRefs.value[index] = el;
  };
  //Адаптируем слайдер при изменении ширины экрана
  const handleResize = () => {
    defineOffsetSize();
    moveSlider(false);
  };
  //Переключение через стрелочки на клавиатуре
  const onKeyDown = (event) => {
    if(rawSlides.length < 2) return;
    if (event.key === 'ArrowLeft') {
      prevSlide();
    } else if (event.key === 'ArrowRight') {
      nextSlide();
    }
  };
  //Переключение через колексико мыши
  const onWheel = (event) => {
    if(rawSlides.length < 2) return;
    if (transitioning) return; 
    if (event.deltaY > 0) {
      nextSlide();
    } else if (event.deltaY < 0) {
      prevSlide();
    }
  };
  //Закрыть слайдер
  const toCloseSlider = () => store.dispatch('Dropdowns/removeTheDropdown', DROPDOWN_NAMES.slider);
</script>

<style lang="scss" scoped>
  @import "./styles.scss";
</style>